// L4 · Resuelve el `linea_id` (lineas_facturacion) por clave natural, consultando la DB. Se usa al
// vincular/desvincular cartelería/hardware por el puente (facturas_sii_vinculaciones.linea_id).
//
// El loader (L2) ya trae `linea_id` en cada fila (canónico desde L6 tramo 4); se usa como fuente
// primaria. Este helper es el fallback para el caso "mes nuevo": el elemento se acaba de agregar al
// array, el trigger de L1 crea la línea, y hay que leer su id fresco (la fila en memoria todavía tenía
// linea_id=null). Sin fallo silencioso (10.177): devuelve null y el caller decide (lanza).

import { client } from "../supabase";
import { derivarClaveHardware } from "./resolverLinea";

/**
 * @param {{negocio_id:(string|number), tipo:string, ano:(string|number), mes:(string|number), tramo?:(number)}} clave
 *        tipo aquí es el de la LÍNEA ('gestion'|'opex'|'hardware'|'contenidos'), no el venta_tipo del puente.
 * @returns {Promise<number|null>} id de la línea o null si no existe.
 */
export const getLineaIdPorClave = async ({ negocio_id, tipo, ano, mes, tramo = 1 }) => {
  if (negocio_id == null || !tipo || ano == null || mes == null) return null;
  const { data, error } = await client
    .from("lineas_facturacion")
    .select("id")
    .eq("negocio_id", String(negocio_id))
    .eq("tipo", tipo)
    .eq("ano", String(ano))
    .eq("mes", String(mes).padStart(2, "0"))
    .eq("tramo", tramo)
    .maybeSingle();
  if (error) throw error;
  return data?.id ?? null;
};

// Mapea el `type` de una fila de VentasFacturas al `tipo` de la línea.
export const tipoLineaDeConcepto = (concepto) => {
  if (concepto?.type === "HW-CAPEX") return "hardware";
  if (concepto?.type === "HW-OPEX") return "opex";
  if (concepto?.type === "Gestión") return "gestion";
  return null;
};

/**
 * Resuelve el linea_id del ELEMENTO en una posición del array (venta_ref_index), leyendo el negocio y
 * derivando su clave natural igual que el backfill L4 / nc_lineas_derivadas (10.1). Para el camino de
 * ConciliacionSII (VincularEmitidaModal), que identifica el concepto por posición.
 * @param {(string|number)} negocioId
 * @param {string} ventaTipo - 'negocios_carteleria' (meses_facturacion) | 'negocios_carteleria_hw' (detalle)
 * @param {(number)} ventaRefIndex - índice 0-based en el array
 * @returns {Promise<number|null>}
 */
export const getLineaIdPorPosicion = async (negocioId, ventaTipo, ventaRefIndex) => {
  if (negocioId == null || ventaRefIndex == null) return null;
  const esHw = ventaTipo === "negocios_carteleria_hw";
  const col = esHw ? "detalle_pagos_hardware" : "meses_facturacion";
  const { data, error } = await client.from("negocios_carteleria").select(col).eq("id", String(negocioId)).single();
  if (error) throw error;
  let arr = data?.[col];
  if (typeof arr === "string") arr = JSON.parse(arr || "[]");
  if (!Array.isArray(arr) || ventaRefIndex < 0 || ventaRefIndex >= arr.length) return null;

  let clave;
  if (esHw) {
    clave = derivarClaveHardware(arr, ventaRefIndex); // {tipo:'hardware', ano, mes, tramo} o null
  } else {
    const e = arr[ventaRefIndex];
    if (!e || !(e.tipo === "gestion" || e.tipo === "opex")) return null;
    clave = { tipo: e.tipo, ano: e.ano, mes: e.mes, tramo: 1 };
  }
  if (!clave) return null;
  return getLineaIdPorClave({ negocio_id: negocioId, ...clave });
};
